import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { MenuItem } from "@/components/design/MenuItem";
import { MultiSelect } from "@/components/design/MultiSelect";
import type { FieldOption } from "@/lib/field-util";
import type { ExtendedPersonFilters } from "@/lib/search-filters";
import { PERSON_AGE_MAX, PERSON_AGE_MIN, PERSON_CHILDREN_MAX, PERSON_CHILDREN_MIN } from "@/lib/search-filters";

export interface PersonField {
  key: string;
  labelId: string;
  kind: "text" | "range" | "multi";
  min?: keyof ExtendedPersonFilters;
  max?: keyof ExtendedPersonFilters;
  lo?: number;
  hi?: number;
  field?: keyof ExtendedPersonFilters;
  optionsKey?: "gender" | "marital" | "country" | "language" | "industry";
  searchable?: boolean;
}

export const PERSON_FIELDS: readonly PersonField[] = [
  { key: "firstName", labelId: "search.fieldFirstName", kind: "text" },
  { key: "lastName", labelId: "search.fieldLastName", kind: "text" },
  { key: "age", labelId: "search.fieldAge", kind: "range", min: "ageMin", max: "ageMax", lo: PERSON_AGE_MIN, hi: PERSON_AGE_MAX },
  { key: "gender", labelId: "search.fieldGender", kind: "multi", field: "genderIds", optionsKey: "gender" },
  { key: "marital", labelId: "search.fieldMaritalStatus", kind: "multi", field: "maritalStatusIds", optionsKey: "marital" },
  { key: "children", labelId: "search.fieldChildren", kind: "range", min: "childCountMin", max: "childCountMax", lo: PERSON_CHILDREN_MIN, hi: PERSON_CHILDREN_MAX },
  { key: "city", labelId: "search.fieldCity", kind: "text" },
  { key: "country", labelId: "search.fieldCountry", kind: "multi", field: "countryIds", optionsKey: "country", searchable: true },
  { key: "citizenship", labelId: "search.fieldCitizenship", kind: "multi", field: "citizenshipIds", optionsKey: "country", searchable: true },
  { key: "nationality", labelId: "search.fieldNationality", kind: "text" },
  { key: "languages", labelId: "search.fieldLanguages", kind: "multi", field: "languageCodes", optionsKey: "language", searchable: true },
  { key: "expertise", labelId: "search.fieldExpertise", kind: "text" },
  { key: "jobTitle", labelId: "search.fieldJobTitle", kind: "text" },
  { key: "department", labelId: "search.fieldDepartment", kind: "text" },
  { key: "industry", labelId: "search.fieldIndustry", kind: "multi", field: "industryIds", optionsKey: "industry", searchable: true },
  { key: "location", labelId: "search.fieldLocation", kind: "text" },
];

// v1 guess: which pill types to surface at the top of the "+ Filter hinzufügen" menu.
// Revise once telemetry is available – single source of truth.
const FREQUENT_FIELD_KEYS: readonly string[] = ["industry", "department", "expertise", "location", "languages"];

export type OptionsByKey = Record<NonNullable<PersonField["optionsKey"]>, readonly FieldOption[]>;

export function isFieldActive(field: PersonField, f: ExtendedPersonFilters): boolean {
  if (field.kind === "text") {
    const v = (f as unknown as Record<string, string>)[field.key];
    return Boolean(v?.trim());
  }
  if (field.kind === "range") {
    const min = (f as unknown as Record<string, number>)[field.min!] ?? field.lo!;
    const max = (f as unknown as Record<string, number>)[field.max!] ?? field.hi!;
    return min > field.lo! || max < field.hi!;
  }
  const arr = (f as unknown as Record<string, readonly string[]>)[field.field!];
  return (arr ?? []).length > 0;
}

export function clearField(field: PersonField, f: ExtendedPersonFilters): ExtendedPersonFilters {
  if (field.kind === "text") return { ...f, [field.key]: "" };
  if (field.kind === "range") return { ...f, [field.min!]: field.lo!, [field.max!]: field.hi! };
  return { ...f, [field.field!]: [] };
}

export function fieldSummary(field: PersonField, f: ExtendedPersonFilters, opts: OptionsByKey): string {
  if (field.kind === "text") {
    return (((f as unknown as Record<string, string>)[field.key]) ?? "").trim();
  }
  if (field.kind === "range") {
    return `${(f as unknown as Record<string, number>)[field.min!] ?? field.lo!}–${(f as unknown as Record<string, number>)[field.max!] ?? field.hi!}`;
  }
  const lookup = opts[field.optionsKey!] ?? [];
  return ((f as unknown as Record<string, readonly string[]>)[field.field!] ?? [])
    .map((id) => lookup.find((o) => o.value === id)?.label ?? id)
    .join(", ");
}

export function PersonFilterBuilder({
  person,
  setPerson,
  optionsByKey,
  onEnter,
  compact = false,
}: {
  person: ExtendedPersonFilters;
  setPerson: React.Dispatch<React.SetStateAction<ExtendedPersonFilters>>;
  optionsByKey: OptionsByKey;
  onEnter?: () => void;
  compact?: boolean;
}) {
  const intl = useIntl();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(() =>
    PERSON_FIELDS.filter((f) => isFieldActive(f, person)).map((f) => f.key),
  );

  const setP = (key: keyof ExtendedPersonFilters) => (value: unknown) =>
    setPerson((f) => ({ ...f, [key]: value } as ExtendedPersonFilters));

  const displayedFields = selected
    .map((key) => PERSON_FIELDS.find((f) => f.key === key))
    .filter((f): f is PersonField => Boolean(f));
  const availableFields = PERSON_FIELDS.filter((f) => !selected.includes(f.key));
  const frequentAvailable = availableFields.filter((f) => FREQUENT_FIELD_KEYS.includes(f.key));
  const restAvailable = availableFields.filter((f) => !FREQUENT_FIELD_KEYS.includes(f.key));
  const editingField = PERSON_FIELDS.find((f) => f.key === editingKey) ?? null;
  const valueCount = PERSON_FIELDS.filter((f) => isFieldActive(f, person)).length;

  const openEditor = (key: string) => {
    setEditingKey(key);
    setAddOpen(false);
  };

  const addField = (key: string) => {
    setSelected((s) => (s.includes(key) ? s : [...s, key]));
    setAddOpen(false);
    setEditingKey(key);
  };

  const removeField = (field: PersonField) => {
    setSelected((s) => s.filter((k) => k !== field.key));
    setPerson((f) => clearField(field, f));
    setEditingKey((k) => (k === field.key ? null : k));
  };

  return (
    <div className={compact ? "" : "px-5 py-4"}>
      <div className="mb-3 text-[10px] tracking-[0.25em] text-ink/45">
        <FormattedMessage
          id="personFilter.heading"
          defaultMessage="Team Player filters{count, plural, =0 {} other { · # active}}"
          values={{ count: valueCount }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {displayedFields.map((f) => {
          const value = isFieldActive(f, person) ? fieldSummary(f, person, optionsByKey) : null;
          const editing = editingKey === f.key;
          const fieldLabel = intl.formatMessage({ id: f.labelId, defaultMessage: f.key });
          return (
            <span
              key={f.key}
              className={`inline-flex items-center rounded-full py-1 pl-2.5 pr-1 text-[11px] transition ${
                editing ? "ring-1 ring-accent/50 " : ""
              }${value ? "bg-ink/[0.06] text-ink/70" : "border border-dashed border-ink/30 text-ink/55"}`}
            >
              <button
                type="button"
                onClick={() => openEditor(f.key)}
                className="inline-flex min-w-0 items-center gap-1"
              >
                <span className="text-ink/45">
                  {fieldLabel}
                  {value ? ":" : ""}
                </span>
                {value ? <span className="max-w-[220px] truncate text-ink/70">{value}</span> : null}
              </button>
              <button
                type="button"
                aria-label={intl.formatMessage(
                  { id: "search.removeFieldLabel", defaultMessage: "Remove {field}" },
                  { field: fieldLabel },
                )}
                onClick={() => removeField(f)}
                className="ml-1 grid h-4 w-4 shrink-0 place-items-center rounded-full text-ink/40 transition hover:bg-ink/10 hover:text-ink"
              >
                <span className="block text-[13px] leading-none">×</span> {/* i18n-allow */}
              </button>
            </span>
          );
        })}

        <div className="relative">
          <Button
            variant="chip"
            size="sm"
            disabled={availableFields.length === 0}
            onClick={() => {
              setAddOpen((o) => !o);
              setEditingKey(null);
            }}
          >
            <FormattedMessage id="personFilter.addFilter" defaultMessage="+ Add filter" />
          </Button>
          {addOpen ? (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setAddOpen(false)} />
              <div className="absolute left-0 top-full z-30 mt-1 max-h-80 w-56 overflow-auto rounded-md bg-page py-1 shadow-lg ring-1 ring-ink/10">
                {availableFields.length === 0 ? (
                  <div className="px-3 py-1.5 text-[12px] text-ink/40">
                    <FormattedMessage id="search.allFiltersAdded" defaultMessage="All filters added" />
                  </div>
                ) : (
                  <>
                    {frequentAvailable.length > 0 ? (
                      <>
                        <div className="px-3 pb-1 pt-1.5 text-[9px] uppercase tracking-[0.2em] text-ink/40">
                          <FormattedMessage id="personFilter.frequent" defaultMessage="Frequent" />
                        </div>
                        {frequentAvailable.map((f) => (
                          <MenuItem
                            key={f.key}
                            onClick={() => addField(f.key)}
                          >
                            {intl.formatMessage({ id: f.labelId, defaultMessage: f.key })}
                          </MenuItem>
                        ))}
                      </>
                    ) : null}
                    {frequentAvailable.length > 0 && restAvailable.length > 0 ? (
                      <div className="my-1 border-t border-ink/5" />
                    ) : null}
                    {restAvailable.length > 0 ? (
                      <>
                        <div className="px-3 pb-1 pt-1.5 text-[9px] uppercase tracking-[0.2em] text-ink/40">
                          <FormattedMessage id="personFilter.allFilters" defaultMessage="All filters" />
                        </div>
                        {restAvailable.map((f) => (
                          <MenuItem
                            key={f.key}
                            onClick={() => addField(f.key)}
                          >
                            {intl.formatMessage({ id: f.labelId, defaultMessage: f.key })}
                          </MenuItem>
                        ))}
                      </>
                    ) : null}
                  </>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {editingField ? (
        <FilterEditor
          field={editingField}
          person={person}
          setP={setP}
          optionsByKey={optionsByKey}
          onClear={() => removeField(editingField)}
          onDone={() => setEditingKey(null)}
          onEnter={() => {
            setEditingKey(null);
            onEnter?.();
          }}
        />
      ) : null}
    </div>
  );
}

function FilterEditor({
  field,
  person,
  setP,
  optionsByKey,
  onClear,
  onDone,
  onEnter,
}: {
  field: PersonField;
  person: ExtendedPersonFilters;
  setP: (key: keyof ExtendedPersonFilters) => (value: unknown) => void;
  optionsByKey: OptionsByKey;
  onClear: () => void;
  onDone: () => void;
  onEnter: () => void;
}) {
  const intl = useIntl();
  return (
    <div className="mt-3 w-full max-w-sm rounded-md bg-ink/[0.06] p-3 ring-1 ring-ink/5">
      <div className="mb-2 flex items-center justify-between gap-4">
        <span className="text-[10px] tracking-[0.15em] text-ink/45">
          <FormattedMessage id={field.labelId} defaultMessage={field.key} />
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClear}
            className="text-[10px] tracking-[0.15em] text-ink/40 hover:text-accent"
          >
            <FormattedMessage id="common.remove" defaultMessage="Remove" />
          </button>
          <button
            type="button"
            onClick={onDone}
            className="text-[10px] tracking-[0.15em] text-ink/55 hover:text-ink"
          >
            <FormattedMessage id="search.done" defaultMessage="Done" />
          </button>
        </div>
      </div>

      {field.kind === "text" ? (
        <FilterTextInput
          value={(person as unknown as Record<string, string>)[field.key] ?? ""}
          onChange={(v) => setP(field.key as keyof ExtendedPersonFilters)(v)}
          onEnter={onEnter}
          autoFocus
        />
      ) : null}
      {field.kind === "range" ? (
        <RangeRow
          min={(person as unknown as Record<string, number>)[field.min!] ?? field.lo!}
          max={(person as unknown as Record<string, number>)[field.max!] ?? field.hi!}
          lo={field.lo!}
          hi={field.hi!}
          onMin={(v) => setP(field.min!)(v)}
          onMax={(v) => setP(field.max!)(v)}
        />
      ) : null}
      {field.kind === "multi" ? (
        <MultiSelect
          options={optionsByKey[field.optionsKey!] ?? []}
          selected={(person as unknown as Record<string, readonly string[]>)[field.field!] ?? []}
          onChange={(arr) => setP(field.field!)(arr)}
          placeholder={intl.formatMessage({ id: "search.any", defaultMessage: "Any" })}
          searchable={field.searchable}
        />
      ) : null}
    </div>
  );
}

function FilterTextInput({
  value,
  onChange,
  onEnter,
  autoFocus = false,
}: {
  value: string;
  onChange: (next: string) => void;
  onEnter?: () => void;
  autoFocus?: boolean;
}) {
  return (
    <input
      type="text"
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && onEnter) onEnter();
      }}
      className="w-full rounded bg-page px-2 py-1.5 text-[13px] text-ink ring-1 ring-ink/10 placeholder:text-ink/40 focus:outline-none focus:ring-1 focus:ring-accent"
    />
  );
}

function RangeRow({
  min,
  max,
  lo,
  hi,
  onMin,
  onMax,
}: {
  min: number;
  max: number;
  lo: number;
  hi: number;
  onMin: (next: number) => void;
  onMax: (next: number) => void;
}) {
  const clamp = (v: string, fallback: number): number => {
    const n = Number.parseInt(v, 10);
    if (Number.isNaN(n)) return fallback;
    return Math.min(hi, Math.max(lo, n));
  };
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={lo}
        max={hi}
        value={min}
        onChange={(e) => onMin(clamp(e.target.value, lo))}
        className="w-full rounded bg-page px-2 py-1.5 text-[13px] text-ink ring-1 ring-ink/10 focus:outline-none focus:ring-1 focus:ring-accent"
      />
      <span className="text-[12px] text-ink/45">
        <FormattedMessage id="search.rangeTo" defaultMessage="to" />
      </span>
      <input
        type="number"
        min={lo}
        max={hi}
        value={max}
        onChange={(e) => onMax(clamp(e.target.value, hi))}
        className="w-full rounded bg-page px-2 py-1.5 text-[13px] text-ink ring-1 ring-ink/10 focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </div>
  );
}
